import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { FUNNEL_TYPE } from '../../../enum/constants';
import { BoardCompanyService } from '../../../services/board-company.service';
import { BoardContactService } from '../../../services/board-contact.service';
import { StackedSidebarService } from '../../../services/stacked-sidebar.service';
import { isStaleTaskError, TaskService } from '../../../services/task.service';
import { SidebarType } from '../../layout/stacked-sidebar/stacked-sidebar.interface';
import { buildUpdatePayload, createTaskForm, fillTaskForm, refillTaskForm } from '../tasks.form';
import {
  ResolvedTaskLink,
  Task,
  TaskDetailResult,
  TaskReminder,
  TaskReminderPayload,
  TaskTimelineEvent,
  TaskUser,
} from '../tasks.types';
import { getLinkKey, getTaskStatusActions, TaskStatusAction } from '../tasks.utils';
import { TaskReminderRow } from '../task-reminders/task-reminders.component';

const byRemindAt = (a: TaskReminder, b: TaskReminder): number =>
  new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime();

/**
 * A task: its fields to edit, the buttons that move it between the statuses, its reminders, and the comments and
 * the history. It sits in a stacked sidebar. The fields are saved with the button, while the status, the reminders
 * and the comments are saved right away.
 */
@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full w-full min-h-0' },
})
export class TaskDetailComponent implements OnChanges {
  @Input() taskId: string | null = null;
  /** Off while a card detail is open under this sidebar: a second card detail would replace the shared card */
  @Input() canOpenCards = true;
  @Output() close = new EventEmitter<TaskDetailResult | void>();

  private readonly taskService = inject(TaskService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = createTaskForm();
  protected task: Task | null = null;
  protected users: TaskUser[] = [];
  protected statusActions: TaskStatusAction[] = [];
  protected timelines: TaskTimelineEvent[] = [];
  protected timelineLoading = false;
  protected loading = false;
  protected saving = false;
  /** A request that changes the task right away is running: the buttons wait for it */
  protected busy = false;
  protected showCloseConfirmation = false;
  protected canLink = false;

  protected readonly actionClasses: Record<TaskStatusAction['tone'], string> = {
    primary: 'bg-emerald-500 text-white hover:bg-emerald-600',
    default: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    danger: 'border border-rose-200 bg-white text-rose-600 hover:bg-rose-50',
  };

  private changed = false;
  private timelineRequest?: Subscription;
  /** The links of the saved task with their names, what the form goes back to when the edits are dropped */
  private savedLinks: ResolvedTaskLink[] = [];

  get hasChanges(): boolean {
    return !!this.task && Object.keys(buildUpdatePayload(this.form, this.task)).length > 0;
  }

  /** The title from the form, so the header follows the edits */
  get displayTitle(): string {
    return this.form.controls.title.value.trim() || this.task?.title || '';
  }

  get creator(): TaskUser | null {
    return this.findUser(this.task?.created_by);
  }

  /** Only the creator accepts a task that is on review or returns it to work */
  get isCreator(): boolean {
    return !!this.task && this.task.created_by === this.taskService.currentUserId;
  }

  /** The reminders as the list shows them, the nearest first */
  get reminders(): TaskReminderRow[] {
    return [...(this.task?.reminders ?? [])].sort(byRemindAt);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskId'] && this.taskId) this.load(this.taskId);
  }

  protected findUser(id: string | null | undefined): TaskUser | null {
    return this.users.find((user) => user.id === id) ?? null;
  }

  // #region status

  protected changeStatus(action: TaskStatusAction): void {
    const task = this.task;
    if (!task || this.busy || this.saving) return;

    this.taskService
      .confirm(action.status === 'CANCELLED' ? 'crm_tasks.actions.cancel_confirm' : null)
      .pipe(
        switchMap(() => {
          this.busy = true;
          return this.taskService.changeStatus(task.id, action.status).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.changed = true;
          this.applyTask(updated);
          this.loadTimeline();
        },
        error: (error) => this.handleError(error),
      });
  }

  // #endregion

  // #region reminders

  protected addReminder(payload: TaskReminderPayload): void {
    const task = this.task;
    if (!task || this.busy || this.saving) return;

    this.busy = true;
    this.taskService
      .createReminder(task.id, payload)
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (reminder) => {
          this.changed = true;
          // A time that is among the reminders already comes back as that reminder
          this.setReminders((reminders) =>
            reminders.some((item) => item.id === reminder.id) ? reminders : [...reminders, reminder],
          );
        },
        error: (error) => this.handleError(error),
      });
  }

  protected removeReminder(row: TaskReminderRow): void {
    const task = this.task;
    if (!task || this.busy || this.saving) return;

    this.busy = true;
    this.taskService
      .deleteReminder(task.id, row.id)
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.changed = true;
          this.setReminders((reminders) => reminders.filter((item) => item.id !== row.id));
        },
        error: (error) => {
          this.handleError(error);
          // Someone deleted it before: it is gone either way
          if (this.taskService.getErrorCode(error) === 'TASK_REMINDER_NOT_FOUND') {
            this.setReminders((reminders) => reminders.filter((item) => item.id !== row.id));
          }
        },
      });
  }

  /** Changes the reminders of the task as it is now: it could have been replaced while the request was running */
  private setReminders(change: (reminders: TaskReminder[]) => TaskReminder[]): void {
    const task = this.task;
    if (task) this.task = { ...task, reminders: change(task.reminders) };
  }

  // #endregion

  // #region links

  protected openLink(link: ResolvedTaskLink): void {
    switch (link.entity_type) {
      case 'card':
        if (!this.canOpenCards) return;

        this.stackedSidebarService
          .openSidebar(
            { type: SidebarType.CardV3, width: '90vw' },
            { card_id: link.entity_id, type: link.funnelType ?? FUNNEL_TYPE.DEAL },
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
        break;
      case 'contact':
        this.contactService
          .openContactDetail(link.entity_id, this.canOpenCards)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
        break;
      case 'company':
        this.companyService
          .openCompanyDetail(link.entity_id, this.canOpenCards)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
        break;
    }
  }

  // #endregion

  // #region saving and closing

  protected cancelChanges(): void {
    if (this.saving || !this.task) return;

    this.showCloseConfirmation = false;
    this.fillForm(this.task, this.savedLinks);
  }

  protected save(closeAfterSave = false): void {
    const task = this.task;
    if (this.saving || this.busy || !task) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.showCloseConfirmation = false;
      return;
    }

    const payload = buildUpdatePayload(this.form, task);
    if (!Object.keys(payload).length) {
      if (closeAfterSave) this.emitClose();
      return;
    }

    this.saving = true;
    this.taskService
      .updateTask(task.id, payload)
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (saved) => {
          this.changed = true;
          this.applyTask(saved, true);
          this.loadTimeline();
          this.taskService.success('crm_tasks.detail.saved');
          if (closeAfterSave) this.emitClose();
        },
        error: (error) => {
          this.showCloseConfirmation = false;
          this.handleError(error);
        },
      });
  }

  /** Called by the header button and by the sidebar backdrop and escape */
  requestClose(): void {
    if (this.saving || this.showCloseConfirmation) return;

    if (!this.hasChanges) {
      this.emitClose();
      return;
    }

    this.showCloseConfirmation = true;
  }

  protected cancelClose(): void {
    this.showCloseConfirmation = false;
  }

  protected discardChangesAndClose(): void {
    if (this.saving) return;

    this.emitClose();
  }

  protected remove(): void {
    const task = this.task;
    if (!task || this.busy || this.saving) return;

    this.taskService
      .confirm('crm_tasks.detail.delete_confirm', { title: task.title })
      .pipe(
        switchMap(() => {
          this.busy = true;
          return this.taskService.deleteTask(task.id).pipe(finalize(() => (this.busy = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.taskService.success('crm_tasks.detail.deleted');
          this.close.emit({ changed: true, deleted: true });
        },
        error: (error) => this.handleError(error),
      });
  }

  private emitClose(): void {
    this.showCloseConfirmation = false;
    this.close.emit(this.changed ? { changed: true } : undefined);
  }

  // #endregion

  // #region loading

  protected loadTimeline(): void {
    const task = this.task;
    if (!task) return;

    this.timelineRequest?.unsubscribe();
    this.timelineLoading = true;
    this.timelineRequest = this.taskService
      .getTimelines(task.id)
      .pipe(
        catchError(() => of([] as TaskTimelineEvent[])),
        finalize(() => (this.timelineLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((timelines) => (this.timelines = timelines));
  }

  private load(taskId: string, showLoader = true): void {
    this.loading = showLoader;
    forkJoin({
      task: this.taskService.getTask(taskId),
      users: this.taskService.getUsers().pipe(catchError(() => of([] as TaskUser[]))),
    })
      .pipe(
        switchMap(({ task, users }) => this.resolveLinks(task).pipe(map((links) => ({ task, users, links })))),
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ task, users, links }) => {
          this.users = users;
          this.canLink = this.taskService.isCrmV2 || links.length > 0;
          this.task = task;
          this.statusActions = getTaskStatusActions(task, this.taskService.currentUserId);
          this.fillForm(task, links);
          this.loadTimeline();
        },
        error: (error) => {
          this.taskService.error(error);
          if (!this.task) this.close.emit();
        },
      });
  }

  /**
   * Takes the task the backend returned. The form is filled with it, except for the fields that have unsaved edits
   * (unless the task was just saved from the form): a status change does not wipe what is being typed, and what
   * someone else changed in the other fields is not sent back with the next save.
   */
  private applyTask(task: Task, refillForm = false): void {
    const previous = this.task;
    const keepEdits = !refillForm && this.hasChanges;
    this.task = task;
    this.statusActions = getTaskStatusActions(task, this.taskService.currentUserId);

    this.resolveLinks(task)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((links) => this.fillForm(task, links, keepEdits ? previous : null));
  }

  /** `keepEditsSince` is the task the edits were made on: its fields that were changed keep their edits */
  private fillForm(task: Task, links: ResolvedTaskLink[], keepEditsSince: Task | null = null): void {
    this.savedLinks = links;
    if (keepEditsSince) refillTaskForm(this.form, keepEditsSince, task, links);
    else fillTaskForm(this.form, task, links);
  }

  /** The names of the links that are known already are kept, only the new ones are asked for */
  private resolveLinks(task: Task): Observable<ResolvedTaskLink[]> {
    const known = new Map(
      [...this.savedLinks, ...this.form.controls.links.value].map((link) => [getLinkKey(link), link]),
    );
    const unknown = task.links.filter((link) => !known.has(getLinkKey(link)));

    return this.taskService.resolveLinks(unknown).pipe(
      map((resolved) => {
        const byKey = new Map([...known, ...resolved.map((link) => [getLinkKey(link), link] as const)]);
        return task.links.map((link) => byKey.get(getLinkKey(link)) as ResolvedTaskLink);
      }),
    );
  }

  /** A conflict or a gone task means that what is on the screen is out of date */
  private handleError(error: unknown): void {
    this.taskService.error(error);
    const code = this.taskService.getErrorCode(error);
    if (!isStaleTaskError(code) || !this.taskId) return;

    if (code === 'TASK_NOT_FOUND') {
      this.close.emit({ changed: true, deleted: true });
      return;
    }
    this.taskService
      .getTask(this.taskId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (task) => {
          this.applyTask(task);
          this.loadTimeline();
        },
        error: (reloadError) => this.taskService.error(reloadError),
      });
  }

  // #endregion
}
